/**
 * Phase-B: Batch deactivation/archive dialog with bulk-reassignment wizard.
 *
 * Flow:
 *   1. Confirm action (deactivate vs archive)
 *   2. If active enrollments exist → choose:
 *        a. Move students to another batch (bulk-reassign first, then deactivate/archive)
 *        b. Keep without reassignment (force_keep=true, expires enrollments)
 *        c. Cancel
 *   3. Toast with summary
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Archive, PauseCircle, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BatchPicker } from "@/components/students/BatchPicker";
import { CapacityWarningDialog } from "@/components/students/CapacityWarningDialog";
import { getCapacityStatus } from "@/lib/capacity";

export const BatchRetirementDialog = ({
  open,
  onOpenChange,
  action,                 // "deactivate" | "archive"
  batch,
  batches,                // pool for the destination picker
  styles, studios, instructors, masterInstructors,
  onCompleted,            // callback after successful flow
}) => {
  const [step, setStep] = useState("confirm");      // confirm | reassign | success
  const [busy, setBusy] = useState(false);
  const [activeCount, setActiveCount] = useState(null);
  const [destBatchId, setDestBatchId] = useState("");
  const [reassignChoice, setReassignChoice] = useState("move");  // move | keep
  const [discontinueConfirmText, setDiscontinueConfirmText] = useState("");
  const [capacityWarn, setCapacityWarn] = useState(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep("confirm");
      setBusy(false);
      setActiveCount(null);
      setDestBatchId("");
      setReassignChoice("move");
      setDiscontinueConfirmText("");
    }
  }, [open, batch]);

  const verb = action === "archive" ? "Archive" : "Deactivate";
  const verbPast = action === "archive" ? "archived" : "deactivated";
  const Icon = action === "archive" ? Archive : PauseCircle;
  const accent = action === "archive" ? "red" : "amber";

  // Filter destination batches: same studio, active, not the source
  const destBatchPool = useMemo(() => {
    if (!batch || !batches) return [];
    return batches.filter(
      (b) => b.batch_id !== batch.batch_id
        && b.studio_id === batch.studio_id
        && (b.status || "active") === "active",
    );
  }, [batch, batches]);

  const callRetire = async (forceKeep) => {
    return api.post(`/batches/${batch.batch_id}/${action}`, { force_keep: forceKeep });
  };

  // Step 1: try the action; if 409 → enter reassign step
  const handleConfirm = async () => {
    setBusy(true);
    try {
      const res = await callRetire(false);
      // Clean: succeeded immediately
      toast.success(`Batch ${verbPast} successfully`);
      onCompleted && onCompleted();
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.action_required === "reassign_or_keep") {
        setActiveCount(detail.active_enrollment_count);
        setStep("reassign");
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || `Failed to ${action}`));
      }
    } finally {
      setBusy(false);
    }
  };

  // Step 2a: move students then deactivate/archive
  const handleMoveAndRetire = async () => {
    if (!destBatchId) {
      toast.error("Pick a destination batch");
      return;
    }
    // Capacity preflight on destination — soft-block with admin override.
    const dest = batches.find((b) => b.batch_id === destBatchId);
    const destCap = getCapacityStatus(dest);
    const incoming = activeCount || 0;
    const projected = (destCap?.enrolled || 0) + incoming;
    const destCapacity = destCap?.capacity || 0;
    if (destCapacity > 0 && projected > destCapacity) {
      setCapacityWarn({
        batchName: dest?.name,
        capacity: destCapacity,
        enrolled: projected,            // show post-move number to make the impact clear
        description: `Moving ${incoming} student(s) into this batch will bring its enrolment to ${projected} / ${destCapacity}.`,
        onConfirm: () => { setCapacityWarn(null); void _doMoveAndRetire(); },
      });
      return;
    }
    await _doMoveAndRetire();
  };

  const _doMoveAndRetire = async () => {    setBusy(true);
    try {
      const r1 = await api.post(`/batches/${batch.batch_id}/bulk-reassign`, { destination_batch_id: destBatchId });
      const moved = r1.data.moved_enrollment_count;
      const r2 = await callRetire(false);   // force_keep=false; should succeed now
      toast.success(`${moved} student(s) reassigned, batch ${verbPast}`);
      onCompleted && onCompleted();
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Reassignment failed"));
    } finally {
      setBusy(false);
    }
  };

  // Step 2b: keep without reassignment — discontinue everyone
  const handleKeepAndRetire = async () => {
    if (discontinueConfirmText !== "DISCONTINUE") {
      toast.error('Type "DISCONTINUE" to confirm');
      return;
    }
    setBusy(true);
    try {
      const r = await callRetire(true);
      toast.success(`Batch ${verbPast} — ${r.data.expired_enrollment_count} enrollment(s) discontinued`);
      onCompleted && onCompleted();
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || `Failed to ${action}`));
    } finally {
      setBusy(false);
    }
  };

  if (!batch) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="batch-retirement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Icon className={`w-5 h-5 text-${accent}-600`} /> {verb} Batch
          </DialogTitle>
        </DialogHeader>

        {/* ===== STEP 1: CONFIRM ===== */}
        {step === "confirm" && (
          <div className="space-y-4">
            <div className={`bg-${accent}-50 border border-${accent}-200 rounded-xl p-3`}>
              <p className="text-sm font-semibold text-slate-900">{batch.name}</p>
              <p className="text-xs text-slate-600 mt-1">
                {action === "archive"
                  ? "Archiving permanently retires this batch. It will be hidden from operational screens but remain in history. Cannot be reversed."
                  : "Deactivating pauses the batch. No new enrollments or sessions will be generated, but it can be reactivated later."}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => onOpenChange(false)} disabled={busy}>
                Cancel
              </Button>
              <Button
                data-testid="confirm-retire-btn"
                className={`flex-1 rounded-xl text-white ${action === "archive" ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"}`}
                onClick={handleConfirm}
                disabled={busy}
              >
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                {verb}
              </Button>
            </div>
          </div>
        )}

        {/* ===== STEP 2: REASSIGN ===== */}
        {step === "reassign" && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-amber-900">
                    This batch has {activeCount} active enrollment(s)
                  </p>
                  <p className="text-[11px] text-amber-700 mt-0.5">
                    Choose how to handle them before {action === "archive" ? "archiving" : "deactivating"}.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <button
                type="button"
                data-testid="reassign-move-btn"
                onClick={() => setReassignChoice("move")}
                className={`w-full text-left p-3 rounded-xl border-2 transition-colors ${reassignChoice === "move" ? "border-violet-500 bg-violet-50" : "border-slate-200 bg-white hover:border-violet-200"}`}
              >
                <div className="flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-violet-600" />
                  <p className="text-sm font-semibold text-slate-900">Move students to another batch</p>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Plain transfer — pricing, end date, attendance & payment history preserved.
                </p>
              </button>

              <button
                type="button"
                data-testid="reassign-keep-btn"
                onClick={() => setReassignChoice("keep")}
                className={`w-full text-left p-3 rounded-xl border-2 transition-colors ${reassignChoice === "keep" ? "border-red-500 bg-red-50" : "border-slate-200 bg-white hover:border-red-200"}`}
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <p className="text-sm font-semibold text-slate-900">Keep without reassignment</p>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Discontinues all {activeCount} enrollments. Cannot be undone.
                </p>
              </button>
            </div>

            {/* Move → batch picker */}
            {reassignChoice === "move" && (
              <div className="space-y-2 pt-2 border-t">
                <Label className="text-xs font-semibold">Destination Batch</Label>
                {destBatchPool.length === 0 ? (
                  <p className="text-xs text-red-600">No active batches in the same studio. Cancel and create one first.</p>
                ) : (
                  <BatchPicker
                    value={destBatchId}
                    batches={destBatchPool}
                    styles={styles}
                    studios={studios}
                    instructors={instructors}
                    masterInstructors={masterInstructors}
                    placeholder="Choose destination batch"
                    testIdPrefix="retire-destination"
                    onChange={setDestBatchId}
                  />
                )}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setStep("confirm")} disabled={busy}>
                    Back
                  </Button>
                  <Button
                    data-testid="confirm-move-and-retire-btn"
                    className="flex-1 rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                    onClick={handleMoveAndRetire}
                    disabled={busy || !destBatchId || destBatchPool.length === 0}
                  >
                    {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                    Move & {verb}
                  </Button>
                </div>
              </div>
            )}

            {/* Keep → typing confirm */}
            {reassignChoice === "keep" && (
              <div className="space-y-2 pt-2 border-t">
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-[11px] text-red-700">
                    This will set status="expired" on all {activeCount} active enrollments and stop renewal tracking. This cannot be undone.
                  </p>
                </div>
                <Label className="text-xs font-semibold">Type "DISCONTINUE" to confirm</Label>
                <Input
                  data-testid="discontinue-confirm-input"
                  value={discontinueConfirmText}
                  onChange={(e) => setDiscontinueConfirmText(e.target.value)}
                  placeholder="DISCONTINUE"
                  className="h-10 rounded-xl"
                />
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setStep("confirm")} disabled={busy}>
                    Back
                  </Button>
                  <Button
                    data-testid="confirm-keep-and-retire-btn"
                    className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white"
                    onClick={handleKeepAndRetire}
                    disabled={busy || discontinueConfirmText !== "DISCONTINUE"}
                  >
                    {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                    Discontinue & {verb}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>

      <CapacityWarningDialog
        open={!!capacityWarn}
        onOpenChange={(o) => { if (!o) setCapacityWarn(null); }}
        batchName={capacityWarn?.batchName}
        capacity={capacityWarn?.capacity}
        enrolled={capacityWarn?.enrolled}
        description={capacityWarn?.description}
        onConfirm={capacityWarn?.onConfirm}
      />
    </Dialog>
  );
};

export default BatchRetirementDialog;
